<template>
  <div class="crm-dashboard">
    <b-row
      v-if="isAdmin || isCompanyAssociatePartner"
      class="portlet-container row-spacing"
    >
      <b-col cols="12" class="portlet-wrapper">
        <b-card no-body class="portlet portlet--global-filters">
          <div class="portlet-header" style="border-radius: 12px 12px 0 0;">
            <h3 class="text-white mb-0">Global Filters</h3>
          </div>
          <b-card-body class="portlet-body">
            <GlobalFilterBar
              ref="globalFilterBar"
              :base-api="baseApi"
              :access-token="accessToken"
              :show-channel-partner-filter="isAdmin"
              :channel-partner-scope-id="companyAssociatePartnerScopeId"
              @filters-changed="handleFiltersChanged"
              @search="handleSearch"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row
      v-if="partnerSummaryCards.length"
      class="portlet-container row-spacing"
    >
      <b-col cols="12" class="portlet-wrapper">
        <b-card no-body class="portlet">
          <div class="portlet-header">
            <h3 class="text-white mb-0">Partner Summary</h3>
          </div>
          <b-card-body class="portlet-body kpi-panel-body">
            <b-alert v-if="fetchError" show variant="danger" class="mb-2">
              {{ fetchError }}
            </b-alert>
            <div v-if="isLoading" class="dashboard-loading text-center py-4">
              <b-spinner variant="primary" label="Loading dashboard" />
              <p class="text-muted mb-0 mt-2">Loading partner summary…</p>
            </div>
            <b-row v-else class="summary-kpi-row">
              <b-col
                v-for="card in partnerSummaryCards"
                :key="card.key"
                cols="12"
                sm="6"
                md="4"
                lg="4"
                xl="4"
                class="summary-kpi-col"
              >
                <div
                  class="summary-kpi-clickable"
                  role="button"
                  tabindex="0"
                  @click="handleSummaryCardClick(card.key)"
                  @keyup.enter="handleSummaryCardClick(card.key)"
                >
                  <kpi-card
                    :title="card.title"
                    :value="card.value"
                    :subtitle="card.subtitle"
                    :color="card.color"
                    :info-text="card.infoText"
                  />
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row
      v-if="summaryCards.length"
      class="portlet-container row-spacing"
    >
      <b-col cols="12" class="portlet-wrapper">
        <b-card no-body class="portlet">
          <div class="portlet-header">
            <h3 class="text-white mb-0">BA Summary</h3>
          </div>
          <b-card-body class="portlet-body kpi-panel-body">
            <b-alert v-if="fetchError" show variant="danger" class="mb-2">
              {{ fetchError }}
            </b-alert>
            <div v-if="isLoading" class="dashboard-loading text-center py-4">
              <b-spinner variant="primary" label="Loading dashboard" />
              <p class="text-muted mb-0 mt-2">Loading summary…</p>
            </div>
            <b-row v-else class="summary-kpi-row">
              <b-col
                v-for="card in summaryCards"
                :key="card.key"
                cols="12"
                sm="6"
                md="4"
                lg="3"
                xl="2"
                class="summary-kpi-col"
              >
                <div
                  class="summary-kpi-clickable"
                  role="button"
                  tabindex="0"
                  @click="handleSummaryCardClick(card.key)"
                  @keyup.enter="handleSummaryCardClick(card.key)"
                >
                  <kpi-card
                    :title="card.title"
                    :value="card.value"
                    :subtitle="card.subtitle"
                    :color="card.color"
                    :info-text="card.infoText"
                  />
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <DashboardDetailModal
      :widget-title="detailWidgetTitle"
      :detail-endpoint="detailEndpoint"
      :detail-params="detailParams"
      :filters="dashboardFilters"
      :visible.sync="detailModalVisible"
      @modal-closed="closeDetailModal"
    />
  </div>
</template>

<script>
//
import { dashboardGet } from '@/components/dashboard/dashboardApi';
import moment from 'moment';
import { BRow, BCol, BCard, BCardBody, BSpinner, BAlert } from 'bootstrap-vue';
import GlobalFilterBar from '@/components/dashboard/GlobalFilterBar.vue';
import KpiCard from '@/components/dashboard/KpiCard.vue';
import DashboardDetailModal from '@/components/dashboard/DashboardDetailModal.vue';

/** CP summary card key → dedicated CP dashboard detail API. */
const SUMMARY_CARD_DETAIL_MAP = {
  total_customer: {
    title: 'Total Investors',
    endpoint: '/dashboard/cp/detail/total_customer',
  },
  site_visit_done: {
    title: 'Site Visit Done',
    endpoint: '/dashboard/cp/detail/site_visit_done',
  },
  follow_up_for_business: {
    title: 'Follow-up for Business',
    endpoint: '/dashboard/cp/detail/follow_up_for_business',
  },
  closure: {
    title: 'Closure',
    endpoint: '/dashboard/cp/detail/closure',
  },
  token: {
    title: 'Booking Amount',
    endpoint: '/dashboard/cp/detail/token',
  },
  commission: {
    title: 'Commission',
    endpoint: '/dashboard/cp/detail/commission',
  },
  interested: {
    title: 'Interested',
    endpoint: '/dashboard/cp/detail/interested',
  },
  site_visit_interested: {
    title: 'Site Visit Interested',
    endpoint: '/dashboard/cp/detail/site_visit_interested',
  },
  total_payment_revenue: {
    title: 'Total Payment / Revenue',
    endpoint: '/dashboard/cp/detail/total_payment_revenue',
  },
  business_partner: {
    title: 'Business Associate',
    endpoint: '/dashboard/cp/detail/business_partner',
  },
  associate_partner: {
    title: 'Associate Partner',
    endpoint: '/dashboard/cp/detail/associate_partner',
  },
  company_associate_partner: {
    title: 'Company Associate Partner',
    endpoint: '/dashboard/cp/detail/company_associate_partner',
  },
};

const SUMMARY_CARD_LOGIC_MAP = {
  total_customer: 'Count of unique investors created for selected date range and selected CP scope.',
  site_visit_done: 'Count of customers/leads where site visit status is marked done in selected filters.',
  follow_up_for_business: 'Sum of deal amount for unique leads in negotiation or closure meeting. Subtitle shows lead count and stage breakdown.',
  closure: 'Count of records marked as closed/closure in selected range.',
  token: 'Count of deals where a booking amount entry exists.',
  commission: 'Sum of commission value for selected scope and date filters; subtitle shows number of commission-linked leads.',
  interested: 'Count of leads marked as interested in follow-up/status workflow.',
  site_visit_interested: 'Count of interested leads that also reached site-visit-interested stage.',
  total_payment_revenue: 'Sum of payment/revenue amount captured for selected CP and date filters.',
  business_partner: 'Count of users with user activity Business Partner in selected CP scope; subtitle shows active vs inactive.',
  associate_partner: 'Count of users with user activity Associate Partner in selected CP scope; subtitle shows active vs inactive.',
  company_associate_partner: 'Count of users with user activity Company associate partner in selected CP scope; subtitle shows active vs inactive.',
};

const emptyPartnerActivityBlock = () => ({
  count: 0,
  active_count: 0,
  inactive_count: 0,
});

const emptyCpSummary = () => ({
  total_customer: { count: 0 },
  site_visit_done: { count: 0 },
  follow_up_for_business: { count: 0, value: 0, negotiation_count: 0, closure_meeting_count: 0 },
  closure: { count: 0 },
  token: { count: 0 },
  commission: { count: 0, value: 0 },
  interested: { count: 0 },
  site_visit_interested: { count: 0 },
  total_payment_revenue: { count: 0, value: 0 },
  business_partner: emptyPartnerActivityBlock(),
  associate_partner: emptyPartnerActivityBlock(),
  company_associate_partner: emptyPartnerActivityBlock(),
});

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  } catch (e) {
    return {};
  }
}

function isAdminUser(user) {
  const role = String(user?.role || '').toLowerCase();
  const type = String(user?.type || '').toLowerCase();
  return role === 'admin' || type === 'admin';
}

function isCompanyAssociatePartnerUser(user) {
  const role = String(user?.role || '').toLowerCase();
  const type = String(user?.type || '').toLowerCase();
  return role === 'companyassociatepartner' || type === 'companyassociatepartner';
}

export default {
  name: 'CrmDashboard',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BSpinner,
    BAlert,
    GlobalFilterBar,
    KpiCard,
    DashboardDetailModal,
  },
  data() {
    return {
      baseApi: process.env.VUE_APP_APIENDPOINT,
      accessToken: localStorage.getItem('accessToken'),
      filters: {
        date_from: moment().startOf('month').format('YYYY-MM-DD'),
        date_to: moment().format('YYYY-MM-DD'),
      },
      cpSummary: emptyCpSummary(),
      isLoading: false,
      initialFetched: false,
      _fetchTimer: null,
      detailModalVisible: false,
      detailWidgetTitle: '',
      detailEndpoint: '',
      detailParams: {},
      fetchError: null,
    };
  },
  computed: {
    isAdmin() {
      return isAdminUser(readUserData());
    },
    isCompanyAssociatePartner() {
      return isCompanyAssociatePartnerUser(readUserData());
    },
    companyAssociatePartnerScopeId() {
      if (!this.isCompanyAssociatePartner) {
        return null;
      }
      const cpId = Number(readUserData().id);
      return Number.isFinite(cpId) && cpId > 0 ? cpId : null;
    },
    /** API params: CP login always scopes to their user id (+ downline on server). */
    dashboardFilters() {
      const payload = { ...this.filters };
      if (this.companyAssociatePartnerScopeId) {
        payload.channel_partner_id = this.companyAssociatePartnerScopeId;
      }
      return payload;
    },
    partnerSummaryCards() {
      const s = this.cpSummary;

      return [
        this.buildPartnerSummaryCard('business_partner', 'Business Associate', s.business_partner, 'teal darken-1'),
        this.buildPartnerSummaryCard('associate_partner', 'Associate Partner', s.associate_partner, 'cyan darken-1'),
        this.buildPartnerSummaryCard(
          'company_associate_partner',
          'Company Associate Partner',
          s.company_associate_partner,
          'indigo darken-1'
        ),
      ];
    },
    followUpForBusinessData() {
      const s = this.cpSummary;
      if (s.follow_up_for_business && typeof s.follow_up_for_business === 'object') {
        return s.follow_up_for_business;
      }
      const negotiation = Number(s.negotiation?.count) || 0;
      const closureMeeting = Number(s.closure_meeting?.count) || 0;
      return {
        count: negotiation + closureMeeting,
        value: 0,
        negotiation_count: negotiation,
        closure_meeting_count: closureMeeting,
      };
    },
    summaryCards() {
      const s = this.cpSummary;
      const followUpForBusiness = this.followUpForBusinessData;
      const commissionCount = Number(s.commission?.count) || 0;
      const commissionValue = Number(s.commission?.value) || 0;

      return [
        {
          key: 'total_customer',
          title: 'Total Investors',
          value: this.formatCount(s.total_customer?.count),
          color: 'blue darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.total_customer,
        },
        {
          key: 'interested',
          title: 'Interested',
          value: this.formatCount(s.interested?.count),
          color: 'green darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.interested,
        },
        {
          key: 'site_visit_interested',
          title: 'Site Visit Interested',
          value: this.formatCount(s.site_visit_interested?.count),
          color: 'orange darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.site_visit_interested,
        },
        {
          key: 'site_visit_done',
          title: 'Site Visit Done',
          value: this.formatCount(s.site_visit_done?.count),
          color: 'purple darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.site_visit_done,
        },
        {
          key: 'follow_up_for_business',
          title: 'Follow-up for Business',
          value: this.formatCurrency(followUpForBusiness.value),
          subtitle: this.followUpForBusinessSubtitle(followUpForBusiness),
          color: 'blue darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.follow_up_for_business,
        },
        {
          key: 'token',
          title: 'Booking Amount',
          value: this.formatCount(s.token?.count),
          color: 'green darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.token,
        },
        {
          key: 'closure',
          title: 'Closure',
          value: this.formatCount(s.closure?.count),
          color: 'orange darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.closure,
        },
        {
          key: 'commission',
          title: 'Commission',
          value: this.formatCurrency(commissionValue),
          subtitle: `${this.formatCount(commissionCount)} leads with commission`,
          color: 'purple darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.commission,
        },
        {
          key: 'total_payment_revenue',
          title: 'Total Payment / Revenue',
          value: this.formatCurrency(s.total_payment_revenue?.value),
          color: 'green darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.total_payment_revenue,
        },
      ];
    },
  },
  mounted() {
    // GlobalFilterBar emits filters on mount; fetch runs from handleFiltersChanged.
    this.$nextTick(() => {
      if (!this.initialFetched) {
        this.initialFetched = true;
        this.fetchSummaryData();
      }
    });
  },
  activated() {
    this.fetchSummaryData();
  },
  beforeDestroy() {
    if (this._fetchTimer) {
      clearTimeout(this._fetchTimer);
    }
  },
  methods: {
    buildPartnerSummaryCard(key, title, block, color) {
      return {
        key,
        title,
        value: this.formatCount(block?.count),
        subtitle: this.partnerActivitySubtitle(block),
        color,
        infoText: SUMMARY_CARD_LOGIC_MAP[key],
      };
    },
    partnerActivitySubtitle(block) {
      if (!block || typeof block !== 'object') {
        return '0 active · 0 inactive';
      }
      const active = Number(block.active_count) || 0;
      const inactive = Number(block.inactive_count) || 0;
      return `${this.formatCount(active)} active · ${this.formatCount(inactive)} inactive`;
    },
    formatCount(value) {
      return Number(value || 0).toLocaleString('en-IN');
    },
    followUpForBusinessSubtitle(block) {
      if (!block || typeof block !== 'object') {
        return null;
      }
      const leadCount = Number(block.count) || 0;
      const negotiation = Number(block.negotiation_count) || 0;
      const closureMeeting = Number(block.closure_meeting_count) || 0;
      if (!leadCount && !negotiation && !closureMeeting) {
        return null;
      }
      const parts = [];
      if (leadCount) {
        parts.push(`${this.formatCount(leadCount)} leads`);
      }
      if (negotiation) {
        parts.push(`${this.formatCount(negotiation)} negotiation`);
      }
      if (closureMeeting) {
        parts.push(`${this.formatCount(closureMeeting)} closure meeting`);
      }
      return parts.join(' · ');
    },
    formatCurrency(value) {
      const n = Number(value || 0);
      return `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    },
    formatRateSubtitle(rate, suffix) {
      if (rate === null || rate === undefined || rate === '') {
        return null;
      }
      const n = Number(rate);
      if (Number.isNaN(n)) {
        return null;
      }
      return `${n.toFixed(2)}% ${suffix}`;
    },
    handleFiltersChanged(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      if (!this.initialFetched) {
        this.initialFetched = true;
        this.fetchSummaryData();
        return;
      }
      this.debouncedFetch();
    },
    handleSearch(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      this.debouncedFetch(true);
    },
    debouncedFetch(forceRefresh = false) {
      if (this._fetchTimer) {
        clearTimeout(this._fetchTimer);
      }
      this._fetchTimer = setTimeout(() => {
        this._fetchTimer = null;
        this.fetchSummaryData(forceRefresh);
      }, 400);
    },
    handleSummaryCardClick(cardKey) {
      const config = SUMMARY_CARD_DETAIL_MAP[cardKey];
      if (!config) {
        return;
      }
      this.openDetailModal(config.title, config.endpoint);
    },
    openDetailModal(widgetTitle, detailEndpoint, detailParams = {}) {
      this.detailWidgetTitle = widgetTitle;
      this.detailEndpoint = detailEndpoint;
      this.detailParams = detailParams;
      this.detailModalVisible = true;
    },
    closeDetailModal() {
      this.detailModalVisible = false;
      this.detailWidgetTitle = '';
      this.detailEndpoint = '';
      this.detailParams = {};
    },
    async fetchSummaryData(forceRefresh = false) {
      if (!this.baseApi || !this.accessToken) {
        return;
      }
      this.isLoading = true;
      this.fetchError = null;
      try {
        const params = { ...this.dashboardFilters };
        if (forceRefresh) {
          params.refresh = 1;
        }
        const response = await dashboardGet(
          this.baseApi,
          this.accessToken,
          '/dashboard/cp/all',
          params
        );
        const data = response.data?.data || {};
        this.cpSummary = {
          ...emptyCpSummary(),
          ...data,
        };
      } catch (error) {
        console.error('Error fetching CP summary:', error);
        this.fetchError =
          error.response?.data?.message || error.message || 'Failed to load CP dashboard summary';
        this.cpSummary = emptyCpSummary();
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.crm-dashboard {
  padding: 20px;
}

.row-spacing {
  margin-top: 20px;
  margin-bottom: 20px;
}

.portlet {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.portlet.portlet--global-filters {
  overflow: visible;
}

.portlet.portlet--global-filters .portlet-body {
  overflow: visible;
}

.portlet:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
}

.portlet-header {
  padding: 10px 16px;
  background: linear-gradient(135deg, #4e73df, #224abe);
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.portlet-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.portlet-body {
  padding: 20px;
  flex-grow: 1;
}

.kpi-panel-body {
  padding: 8px 12px 16px;
}

.summary-kpi-row {
  margin-left: -6px;
  margin-right: -6px;
}

.summary-kpi-col {
  padding-left: 6px;
  padding-right: 6px;
  margin-bottom: 12px;
}

.summary-kpi-clickable {
  cursor: pointer;
  height: 100%;
}

.summary-kpi-clickable:focus {
  outline: 2px solid #4e73df;
  outline-offset: 2px;
  border-radius: 8px;
}
</style>
